/**
 * Minimal incremental text/event-stream parser for fetch()+ReadableStream
 * consumption. We can't use EventSource for the relay push channel because it
 * can't set the Authorization header — fetch-streaming can, and requireAuth
 * on the relay runs unchanged.
 */

export interface SseEvent {
  event: string;
  data: string;
  id?: string;
}

/**
 * Returns a chunk feeder. Call with each decoded text chunk; complete events
 * dispatch to `onEvent`, comment lines (heartbeats) to `onComment`.
 */
export function createSseParser(
  onEvent: (e: SseEvent) => void,
  onComment?: (line: string) => void,
): (chunk: string) => void {
  let buffer = '';
  let event = 'message';
  let data: string[] = [];
  let id: string | undefined;

  const dispatch = () => {
    if (data.length > 0) {
      onEvent({ event, data: data.join('\n'), id });
    }
    event = 'message';
    data = [];
    id = undefined;
  };

  return (chunk: string) => {
    buffer += chunk;
    let nl: number;
    while ((nl = buffer.indexOf('\n')) >= 0) {
      let line = buffer.slice(0, nl);
      buffer = buffer.slice(nl + 1);
      if (line.endsWith('\r')) line = line.slice(0, -1);
      if (line === '') {
        dispatch();
        continue;
      }
      if (line.startsWith(':')) {
        onComment?.(line);
        continue;
      }
      const colon = line.indexOf(':');
      const field = colon === -1 ? line : line.slice(0, colon);
      let value = colon === -1 ? '' : line.slice(colon + 1);
      if (value.startsWith(' ')) value = value.slice(1);
      if (field === 'event') event = value;
      else if (field === 'data') data.push(value);
      else if (field === 'id') id = value;
      // 'retry' and unknown fields ignored (we manage our own backoff).
    }
  };
}
